import Store from '@ember-data/store';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import {
  type FormListSelectionChangedArgs,
  FormListSelectionOption,
} from 'donor-elf-web/components/form/list-selection';
import {
  ContactTableCellState,
  ContactTableRow,
} from 'donor-elf-web/controllers/funds/fund/contacts';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import { isNothing } from 'donor-elf-web/lib/object-utils';
import AbilitiesService from 'donor-elf-web/services/abilities';
import ContactsCache, {
  type ContactCustomPropertyInfo,
  type ContactCustomPropertyType,
} from 'donor-elf-web/services/contacts-cache';
import validateContactCustomProperty from 'donor-elf-web/validations/contact-custom-property';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import PubSub from 'pubsub-js';

const CELL_STARTED_EDITING_TOPIC = 'contact-cell-started-editing';

interface ComponentArgs {
  contactTableRow: ContactTableRow;
  cellState: ContactTableCellState;
}

let NumberOrCurrencyType: { [type in ContactCustomPropertyType]+?: true } = {
  n: true,
  c: true,
};

export default class ContactPropertyCellComponent extends Component<ComponentArgs> {
  constructor(owner: unknown, args: ComponentArgs) {
    super(owner, args);
    this.pubsubToken = PubSub.subscribe(
      CELL_STARTED_EDITING_TOPIC,
      this.handleSomeCellStartedEditing,
    );
  }

  @service declare abilities: AbilitiesService;
  @service declare contactsCache: ContactsCache;
  @service declare store: Store;

  pubsubToken?: string;

  tdElement?: HTMLTableCellElement | null;

  get propertyInfo(): ContactCustomPropertyInfo | undefined {
    return this.contactsCache.customPropertiesByLowecaseLabel[
      this.args.cellState.propertyName.toLowerCase()
    ];
  }

  get isReadonlyProperty() {
    return this.propertyInfo?.r === true;
  }

  get isNumberOrCurrencyType() {
    if (this.propertyInfo == null) {
      return false;
    } else {
      return NumberOrCurrencyType[this.propertyInfo.t] || false;
    }
  }

  // make sure you call `this.args.contactTableRow.fireCellStatesChanged()` after calling this method
  populateGroupOptions() {
    let selectedGroups = this.args.contactTableRow.contact.groups || [];
    this.args.cellState.groupOptions = sortByProperties(
      this.contactsCache.sortedGroups.map((x, index) => {
        return new FormListSelectionOption({
          val: x,
          selected: selectedGroups.includes(x),
          index,
        });
      }),
      ['selected:desc', 'index:asc'],
    );
    this.args.cellState.selectedGroupOptions =
      this.args.cellState.groupOptions.filter((x) => x.selected);
  }

  get selectedGroupOptionsIsSameAsContactGroups() {
    return (
      this.args.cellState
        .selectedGroupOptions!.map((x) => x.val)
        .sort()
        .join(',') ===
      (this.args.contactTableRow.contact.sortedGroups || []).join(',')
    );
  }

  @action
  handleSomeCellStartedEditing(
    _message: string,
    data: { cellState: ContactTableCellState },
  ) {
    // if this cell is editing and then another cell started editing, then reset
    // this cell if there have been no changes made
    if (
      this.args.cellState?.editing &&
      data.cellState !== this.args.cellState
    ) {
      if (this.args.cellState.changeset?.get('isPristine')) {
        this.args.contactTableRow.resetCellState(this.args.cellState);
      } else if (
        this.args.cellState.propertyName === 'groups' &&
        this.selectedGroupOptionsIsSameAsContactGroups
      ) {
        this.args.contactTableRow.resetCellState(this.args.cellState);
      } else if (!this.args.cellState.isCustomProperty) {
        this.args.contactTableRow.resetCellState(this.args.cellState);
      }
    }
  }

  @action
  templateDidInsert(el: HTMLElement) {
    if (this.abilities.hasAbility('edit_contact')) {
      this.tdElement = el.closest('td');
      this.tdElement!.addEventListener('click', this.onTdCellClicked);
    }
  }

  @action
  templateWillDestroy() {
    if (this.abilities.hasAbility('edit_contact')) {
      this.tdElement!.removeEventListener('click', this.onTdCellClicked);
    }
  }

  willDestroy() {
    if (this.pubsubToken) {
      PubSub.unsubscribe(this.pubsubToken!);
    }
  }

  @action
  onTdCellClicked() {
    this.toggleEditing();
  }

  toggleEditing() {
    if (
      !this.args.cellState.isCustomProperty &&
      this.args.cellState.propertyName !== 'groups'
    ) {
      this.args.cellState.editing = !this.args.cellState.editing;
      this.args.contactTableRow.fireCellStatesChanged();
      PubSub.publish(CELL_STARTED_EDITING_TOPIC, {
        cellState: this.args.cellState,
      });
      return;
    }
    if (this.args.cellState.editing || this.args.cellState.saving) {
      if (this.isReadonlyProperty) {
        this.args.cellState.editing = false;
        this.args.contactTableRow.fireCellStatesChanged();
      }
      return;
    }

    this.args.cellState.editing = true;
    if (this.args.cellState.isCustomProperty) {
      let validation = validateContactCustomProperty({
        contactsCache: this.contactsCache,
        isNewProperty: false,
        allowEmptyVal: true,
      });
      let property =
        this.args.contactTableRow.contact.customPropertiesByLabel[
          this.args.cellState.propertyName
        ];
      if (property == null) {
        let { propertyInfo } = this;
        property = {
          l: propertyInfo!.l,
          t: propertyInfo!.t,
          v: undefined,
        };
      }
      this.args.cellState.changeset = new Changeset(
        { ...property },
        lookupValidator(validation),
        validation,
      );
    } else if (this.args.cellState.propertyName === 'groups') {
      this.populateGroupOptions();
    }
    this.args.contactTableRow.fireCellStatesChanged();

    PubSub.publish(CELL_STARTED_EDITING_TOPIC, {
      cellState: this.args.cellState,
    });
  }

  @action
  handleGroupSelectionsChanged(args: FormListSelectionChangedArgs) {
    this.args.cellState.selectedGroupOptions = args.selectedListOptions;
  }

  @action
  async save() {
    if (this.args.cellState.isCustomProperty) {
      this.saveCustomProperty();
    } else if (this.args.cellState.propertyName === 'groups') {
      this.saveGroups();
    }
  }

  get valueWasNothing() {
    return isNothing(this.args.cellState.changeset?.get('data')?.v);
  }

  get valueIsCurrentlyNothing() {
    return isNothing(this.args.cellState.changeset?.get('v'));
  }

  async saveCustomProperty() {
    if (this.valueIsCurrentlyNothing) {
      if (this.valueWasNothing) {
        this.args.cellState.editing = false;
        this.args.contactTableRow.fireCellStatesChanged();
      } else {
        this.remove();
      }
    } else {
      let { changeset } = this.args.cellState;
      await changeset.validate();
      if (changeset.get('isValid')) {
        try {
          this.args.cellState.saving = true;
          this.args.contactTableRow.fireCellStatesChanged();
          changeset.execute();
          let saveCustomProperty = this.store.createRecord('custom-property', {
            contactId: this.args.contactTableRow.contact.id,
            l: changeset.get('l'),
            t: changeset.get('t'),
            v: changeset.get('v'),
            o: changeset.get('o'),
          });
          this.args.cellState.tempCustomProperty = {
            l: saveCustomProperty.l,
            t: saveCustomProperty.t!,
            v: saveCustomProperty.v,
          };
          this.args.contactTableRow.fireCellStatesChanged();
          await saveCustomProperty.save();
          await this.args.contactTableRow.contact.reload();
          if (saveCustomProperty.customPropertyInfo != null) {
            this.contactsCache.updateCustomPropertyInfo(
              saveCustomProperty.customPropertyInfo,
            );
          }
          this.args.contactTableRow.resetCellState(this.args.cellState);
        } catch (e) {
          this.args.cellState.saveError = e;
          this.args.contactTableRow.fireCellStatesChanged();
        }
      } else {
        this.args.cellState.showValidationErrors = true;
        this.args.contactTableRow.fireCellStatesChanged();
      }
    }
  }

  async saveGroups() {
    this.args.cellState.saving = true;
    this.args.cellState.saveError = undefined;
    this.args.contactTableRow.fireCellStatesChanged();
    let contact = this.args.contactTableRow.contact;
    let previousGroups = contact.groups;
    contact.groups = this.args.cellState.selectedGroupOptions!.map(
      (x) => x.val,
    );
    try {
      await contact.save();
      this.args.contactTableRow.resetCellState(this.args.cellState);
    } catch (error) {
      this.args.cellState.saveError = error;
      contact.groups = previousGroups;
      this.args.contactTableRow.fireCellStatesChanged();
    }
  }

  @action
  clearError() {
    this.args.contactTableRow.resetCellState(this.args.cellState);
  }

  async remove() {
    try {
      this.args.cellState.saving = true;
      this.args.cellState.removing = true;
      this.args.contactTableRow.fireCellStatesChanged();
      let { changeset } = this.args.cellState;
      changeset.execute();
      await this.store
        .createRecord('custom-property', {
          contactId: this.args.contactTableRow.contact.id,
          l: changeset.get('l'),
          shouldDestroy: true,
        })
        .save();
      await this.args.contactTableRow.contact.reload();
      this.args.contactTableRow.resetCellState(this.args.cellState);
    } catch (e) {
      this.args.cellState.saveError = e;
      this.args.contactTableRow.fireCellStatesChanged();
    }
  }

  @action
  cancel() {
    this.args.contactTableRow.resetCellState(this.args.cellState);
  }
}
